/**
 * LA FICHE — « elle se lit comme une fiche de personnage sur papier, pas comme un tableau de bord »
 * (design §3).
 *
 * Les six caractéristiques en grand, ET LE FAIT QU'ELLES NE BOUGERONT JAMAIS DOIT SE VOIR : ce sont
 * les seuls nombres du jeu qui sont définitifs. D'où la mention gravée sous le bloc, et l'absence
 * totale de commande à cet endroit — rien à cliquer sur ce qui ne change pas.
 */
import { useRessource } from "../api/chargement.js";
import type { HerosEnVue, Objet, ObjetConnu } from "../api/types.js";
import { Glyphe } from "../composants/Glyphe.js";
import { Jauge } from "../composants/Jauge.js";
import { Sceau } from "../composants/Sceau.js";
import { Filet, Parchemin } from "../composants/Parchemin.js";
import {
  ARCHETYPES,
  BLESSURES,
  CARACTERISTIQUES,
  EMPLACEMENTS,
  ETATS,
  ORDRE_DES_CARACTERISTIQUES,
  RARETES,
  catalogueDe,
  estSurcharge,
  nomDObjet,
  poidsDit
} from "../lexique/mots.js";
import { Lien } from "../routeur.js";
import { t } from "../lexique/langue.js";

export function BlocDeCaracteristiques({ heros }: { heros: HerosEnVue }) {
  return (
    <div className="caracteristiques">
      {ORDRE_DES_CARACTERISTIQUES.map((c) => {
        const mot = CARACTERISTIQUES[c];
        const effectif = heros.statsEffectives[c];
        const base = heros.stats[c];
        const ecart = effectif - base;
        const cicatrisee = heros.cicatrices.includes(c);
        return (
          <div className="caracteristiques__case" key={c}>
            <span className="capitales">{mot.nom}</span>
            <span className="caracteristiques__valeur nombre" style={{ color: mot.teinte }}>
              {effectif}
            </span>
            <span className="faible">
              {t("base")} <span className="nombre">{base}</span>
              {ecart !== 0 ? (
                <span className="nombre" style={{ color: ecart > 0 ? "var(--herbe)" : "var(--sang)" }}>
                  {" "}
                  {ecart > 0 ? "+" : ""}
                  {ecart}
                </span>
              ) : null}
              {cicatrisee ? <span style={{ color: "var(--sang)" }}> {t("· cicatrice")}</span> : null}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function Equipement({ heros, catalogue }: { heros: HerosEnVue; catalogue: Map<string, ObjetConnu> }) {
  const clefs = Object.keys(heros.equipement);
  return (
    <dl className="equipement">
      {clefs.map((clef) => {
        const objetId = heros.equipement[clef];
        const objet = objetId ? catalogue.get(objetId) : null;
        return (
          <div key={clef} className={objetId ? "equipement__ligne" : "equipement__ligne equipement__ligne--vide"}>
            <dt className="capitales">{EMPLACEMENTS[clef] ?? clef}</dt>
            <dd>
              {objetId ? (
                <span style={{ color: objet ? RARETES[objet.rarete]?.teinte : undefined }}>
                  {nomDObjet(objetId, catalogue)}
                </span>
              ) : (
                <span className="faible">—</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function FicheDuHeros({ herosId }: { herosId: string }) {
  const { donnees: heros } = useRessource<HerosEnVue>(`/heros/${herosId}`, (charge: { heros: HerosEnVue }) => charge.heros);
  const { donnees: objets } = useRessource<Objet[]>("/public/objets", (charge: { objets: Objet[] }) => charge.objets);
  // Hors partie, on n'a que le socle : un objet trouvé au nœud 412 s'y dira « objet inconnu »
  // plutôt que de faire tomber la fiche.
  const catalogue = catalogueDe(objets ?? []);

  if (!heros) return <div className="page" aria-busy="true" />;
  const mort = heros.status === "dead";

  return (
    <div className="page arrivee">
      <header className="entete-decran">
        <div>
          <h1>{heros.nom}</h1>
          <p className="douce">
            {ARCHETYPES[heros.archetype]?.nom ?? heros.archetype} — {ARCHETYPES[heros.archetype]?.devise}
          </p>
        </div>
        {mort ? <Sceau empreinte="mort" taille={64} titre={t("Mort")} /> : null}
      </header>

      <div className="fiche">
        <div className="pile pile--large">
          <Parchemin variante="leve">
            <BlocDeCaracteristiques heros={heros} />
            <p className="grave">
              {t("Ces six nombres ont été fixés à la création et ne changeront plus jamais. Toute la puissance qui vous reste à gagner est dans ce que vous porterez.")}
            </p>
          </Parchemin>

          <Parchemin titre={t("Le corps")}>
            <div className="rang" style={{ gap: "var(--e5)" }}>
              <div className="pile pile--serree">
                <span className="capitales">{t("Points de vie")}</span>
                <span className="nombre" style={{ fontSize: "var(--pas-3)" }}>
                  {heros.pv} / {heros.pvMax}
                </span>
                <Jauge valeur={heros.pv} maximum={heros.pvMax} espece="pv" largeur={190} />
              </div>
              {heros.essenceMax > 0 ? (
                <div className="pile pile--serree">
                  <span className="capitales">{t("Essence")}</span>
                  <span className="nombre" style={{ fontSize: "var(--pas-3)" }}>
                    {heros.essence} / {heros.essenceMax}
                  </span>
                  <Jauge valeur={heros.essence} maximum={heros.essenceMax} espece="essence" largeur={190} />
                </div>
              ) : null}
            </div>

            {heros.etats.length ? (
              <div className="rang" style={{ marginTop: "var(--e4)" }}>
                {heros.etats.map((e) => (
                  <span key={e.etat} className="etat" title={ETATS[e.etat].effet}>
                    <Glyphe nom={e.etat} taille={15} />
                    {ETATS[e.etat].nom}
                  </span>
                ))}
              </div>
            ) : null}

            {heros.blessures.length ? (
              <div className="pile pile--serree" style={{ marginTop: "var(--e4)" }}>
                <span className="capitales">{t("Blessures graves")}</span>
                {heros.blessures.map((b, i) => (
                  <span key={`${b}-${i}`}>
                    <strong>{BLESSURES[b].nom}</strong> <span className="faible">— {BLESSURES[b].effet}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </Parchemin>
        </div>

        <aside className="pile pile--large">
          <Parchemin variante="creux" titre={t("Équipé")}>
            <Equipement heros={heros} catalogue={catalogue} />
            <div className="rang rang--espace" style={{ marginTop: "var(--e4)" }}>
              <span className="capitales">{t("Indice de puissance")}</span>
              <span className="nombre">
                {heros.ipe} <span className="faible">· {heros.palier}</span>
              </span>
            </div>
          </Parchemin>

          <Parchemin titre={t("Le sac")}>
            <div className="rang rang--espace">
              <span className="capitales">{t("Charge")}</span>
              <span className={estSurcharge(heros.surcharge) ? "nombre" : "nombre faible"} style={estSurcharge(heros.surcharge) ? { color: "var(--sang)" } : undefined}>
                {poidsDit(heros.poids)} / {poidsDit(heros.capacite)}
              </span>
            </div>
            <div className="rang rang--espace">
              <span className="capitales">{t("Emplacements")}</span>
              <span className="nombre faible">
                {heros.slots.occupes} / {heros.slots.disponibles}
              </span>
            </div>
            <Filet />
            <ul className="sac">
              {heros.sac.map((pile) => (
                <li key={pile.objetId}>
                  <span>{nomDObjet(pile.objetId, catalogue)}</span>
                  <span className="nombre faible">{t("×")}{pile.quantite}</span>
                </li>
              ))}
              {!heros.sac.length ? <li className="faible">{t("Rien.")}</li> : null}
            </ul>
          </Parchemin>

          {heros.status === "alive" ? (
            <Lien vers="/aventures" className="bouton bouton--grand" style={{ textAlign: "center", textDecoration: "none" }}>
              {t("Choisir une aventure")}
            </Lien>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
